import { useEffect, useState } from 'react';
import { Button, Center, Group, Loader, Stack, Text, TextInput } from '@mantine/core';
import { IconDeviceFloppy, IconMail, IconRefresh } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { SectionCard } from './SectionCard';
import { useAuth } from '../contexts/AuthContext';
import {
  SUPPORT_EMAIL_MAX, SUPPORT_EMAIL_RE, supportEmailQueryKey, useSupportEmail,
} from '../hooks/useSupportEmail';

/** Serverga yuboriladigan shakl: bo'shliqsiz, kichik harfda (server ham xuddi shunday qiladi). */
const normalize = (v: string) => v.trim().toLowerCase();

/**
 * MAKTAB SUPPORT EMAIL'I.
 *
 * Huquqiy hujjatlardagi (Foydalanish shartlari, Maxfiylik siyosati, Cookie)
 * "Savollar uchun: …" qatorida — panelda ham, mobil ilovada ham — AYNAN shu
 * maktabning manzili chiqadi. Ilgari u yerda platformaning umumiy manzili qattiq
 * yozilgan edi va ota-ona savolini maktabga emas, platformaga yuborardi.
 *
 * Bo'sh saqlash = tozalash (`null`): hujjatlarda email ko'rsatilmaydi.
 */
export function SupportEmailSettings() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: current, isLoading, isError, isFetching, refetch } = useSupportEmail();

  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  /* Serverdan kelgach maydon to'ldiriladi — faqat BIRINCHI marta, foydalanuvchi
     yozayotgan paytda qiymat ustiga yozib yuborilmasin. */
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    if (filled || current === undefined) return;
    setValue(current ?? '');
    setFilled(true);
  }, [current, filled]);

  const save = useMutation({
    mutationFn: async (supportEmail: string | null) =>
      (await api.put<{ supportEmail: string | null }>('/tenant/support-email', { supportEmail })).data,
    onSuccess: (res) => {
      const saved = typeof res?.supportEmail === 'string' ? res.supportEmail : null;
      setValue(saved ?? '');
      setError(null);
      /* Shu sessiya kaliti: huquqiy sahifalar ham darhol yangi qiymatni oladi. */
      qc.setQueryData(supportEmailQueryKey(user), saved);
      notifications.show({ color: 'green', message: saved ? t('supportEmail.saved') : t('supportEmail.cleared') });
    },
    onError: (e: unknown) =>
      notifications.show({
        color: 'red',
        message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
      }),
  });

  const submit = () => {
    const v = normalize(value);
    if (v && v.length > SUPPORT_EMAIL_MAX) return setError(t('supportEmail.tooLong'));
    if (v && !SUPPORT_EMAIL_RE.test(v)) return setError(t('supportEmail.invalid'));
    setError(null);
    save.mutate(v || null);
  };

  const unchanged = normalize(value) === (current ?? '');

  return (
    <SectionCard
      title={t('supportEmail.section')}
      subtitle={t('supportEmail.sectionDesc')}
      icon={IconMail}
      maw={520}
      mb="md"
    >
      {isLoading ? (
        <Center py="md"><Loader size="sm" /></Center>
      ) : isError ? (
        <Stack gap="sm" align="flex-start">
          <Text size="sm" c="red">{t('supportEmail.loadError')}</Text>
          <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />}
            loading={isFetching} onClick={() => void refetch()}>
            {t('supportEmail.retry')}
          </Button>
        </Stack>
      ) : (
        // `noValidate`: brauzerning o'z (panel tilida bo'lmagan) email oynachasi emas,
        // bizning i18n xatomiz ko'rsatilsin.
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate>
          <Stack gap="sm">
            <TextInput
              type="email"
              label={t('supportEmail.label')}
              placeholder={t('supportEmail.placeholder')}
              description={t('supportEmail.hint')}
              leftSection={<IconMail size={16} />}
              maxLength={SUPPORT_EMAIL_MAX}
              autoComplete="email"
              value={value}
              error={error}
              onChange={(e) => { setValue(e.currentTarget.value); if (error) setError(null); }}
            />
            <Group justify="space-between" wrap="nowrap" align="center">
              <Text fz="xs" lh={1.4} c="dimmed">
                {current ? t('supportEmail.current', { email: current }) : t('supportEmail.notSet')}
              </Text>
              <Button type="submit" loading={save.isPending} disabled={unchanged}
                leftSection={<IconDeviceFloppy size={16} />} style={{ flexShrink: 0 }}>
                {t('common.save')}
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </SectionCard>
  );
}
